import {
  Alert,
  Box,
  Button,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';
import { COLUMNS, COLUMN_COUNT, PAGE_SIZE } from '../constants';
import PartnerPreferenceRow from './PartnerPreferenceRow';

const HEAD_CELL_SX = {
  backgroundColor: colors.tableHeaderNavy,
  color: colors.white,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  '& .MuiTableSortLabel-root': { color: colors.white },
  '& .MuiTableSortLabel-root:hover': { color: colors.white },
  '& .MuiTableSortLabel-root.Mui-active': { color: colors.white },
  '& .MuiTableSortLabel-icon': { color: `${colors.white} !important` },
};

const LoadingRows = () =>
  Array.from({ length: 5 }, (_, index) => (
    <TableRow key={`skeleton-${index}`}>
      {COLUMNS.map((column) => (
        <TableCell key={column.id}>
          <Skeleton variant='text' />
        </TableCell>
      ))}
    </TableRow>
  ));

const PartnerPreferenceTable = ({
  partners,
  totalCount,
  loading,
  error,
  onRetry,
  page,
  onPageChange,
  sortOrder,
  onSortOrderChange,
  expandedPartnerId,
  onToggleExpand,
  childrenByPartnerId,
  childLoadingId,
  childErrorById,
  onChildRetry,
  canManage,
  hasActiveSearch,
  searchMatchedPartner,
  onAddForSearchedPartner,
  onAdd,
  onAddForPartner,
  onChange,
  onRemove,
  onCopyPartnerId,
  partnerStatusById,
}) => {
  const pageCount = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  const direction = sortOrder === 'ASC' ? 'asc' : 'desc';

  const handleSort = () => onSortOrderChange(sortOrder === 'ASC' ? 'DESC' : 'ASC');

  const renderEmpty = () => {
    // Two distinct empty states. A search that matched nothing is not the same
    // situation as "nothing is configured yet", and the difference is the whole
    // point: the list only holds partners that HAVE preferences, so searching for
    // a partner that exists but has none must not look like a broken screen.
    if (hasActiveSearch) {
      return (
        <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
          {searchMatchedPartner ? (
            <>
              <Typography variant='body1' sx={{ fontWeight: 700, mb: 0.5 }}>
                {`${searchMatchedPartner.partnerName} has no NDC preferences yet`}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                This partner exists but nothing is configured for it.
              </Typography>
              {canManage && (
                <Button
                  variant='contained'
                  size='small'
                  onClick={() => onAddForSearchedPartner(searchMatchedPartner)}
                >
                  {`Add preference for ${searchMatchedPartner.partnerName}`}
                </Button>
              )}
            </>
          ) : (
            <>
              <Typography variant='body1' sx={{ fontWeight: 700, mb: 0.5 }}>
                No match
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                No partner matches that search.
              </Typography>
            </>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
        <Typography variant='h6' sx={{ fontWeight: 700, mb: 0.5 }}>
          No partner preferences yet
        </Typography>
        <Typography
          variant='body2'
          sx={{ color: 'text.secondary', maxWidth: 560, mx: 'auto', mb: 2 }}
        >
          Set a preferred NDC when a partner must receive one specific NDC for a drug — a
          manufacturer requirement, a contract, or a recall. Without a preference, allocation works
          exactly as it does today.
        </Typography>
        {canManage && (
          <Button variant='contained' size='small' onClick={onAdd}>
            + New Partner Preference
          </Button>
        )}
      </Box>
    );
  };

  if (error) {
    return (
      <Alert
        severity='error'
        action={
          <Button color='inherit' size='small' onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant='outlined'>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                align={column.align ?? 'left'}
                sx={{ ...HEAD_CELL_SX, width: column.width }}
                sortDirection={column.sortable ? direction : false}
              >
                {column.sortable ? (
                  <TableSortLabel active direction={direction} onClick={handleSort}>
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading && <LoadingRows />}

          {!loading && partners.length === 0 && (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 'none' }}>
                {renderEmpty()}
              </TableCell>
            </TableRow>
          )}

          {!loading &&
            partners.map((partner) => (
              <PartnerPreferenceRow
                key={partner.partnerId}
                partner={partner}
                expanded={expandedPartnerId === partner.partnerId}
                onToggle={() => onToggleExpand(partner.partnerId)}
                preferences={childrenByPartnerId[partner.partnerId]}
                loading={childLoadingId === partner.partnerId}
                error={childErrorById[partner.partnerId] ?? ''}
                onRetry={() => onChildRetry(partner.partnerId)}
                canManage={canManage}
                onAddForPartner={() => onAddForPartner(partner)}
                onChange={(preference) => onChange(partner, preference)}
                onRemove={(preference) => onRemove(partner, preference)}
                onCopyPartnerId={onCopyPartnerId}
                partnerStatus={partnerStatusById[partner.partnerId]}
              />
            ))}
        </TableBody>

        {!loading && pageCount > 1 && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Pagination
                    size='small'
                    count={pageCount}
                    page={page}
                    onChange={(_, nextPage) => onPageChange(nextPage)}
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};

PartnerPreferenceTable.propTypes = {
  partners: PropTypes.arrayOf(PropTypes.shape({ partnerId: PropTypes.string })).isRequired,
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  sortOrder: PropTypes.oneOf(['ASC', 'DESC']).isRequired,
  onSortOrderChange: PropTypes.func.isRequired,
  expandedPartnerId: PropTypes.string,
  onToggleExpand: PropTypes.func.isRequired,
  childrenByPartnerId: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.shape({ productId: PropTypes.string }))
  ).isRequired,
  childLoadingId: PropTypes.string,
  childErrorById: PropTypes.objectOf(PropTypes.string).isRequired,
  onChildRetry: PropTypes.func.isRequired,
  canManage: PropTypes.bool.isRequired,
  hasActiveSearch: PropTypes.bool.isRequired,
  searchMatchedPartner: PropTypes.shape({
    partnerUuid: PropTypes.string,
    partnerName: PropTypes.string,
  }),
  onAddForSearchedPartner: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onAddForPartner: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onCopyPartnerId: PropTypes.func,
  partnerStatusById: PropTypes.objectOf(PropTypes.string),
};

PartnerPreferenceTable.defaultProps = {
  totalCount: 0,
  loading: false,
  error: '',
  expandedPartnerId: null,
  childLoadingId: null,
  searchMatchedPartner: null,
  onCopyPartnerId: undefined,
  partnerStatusById: {},
};

export default PartnerPreferenceTable;
